import { TruthyValueArray } from '../../../../src/services/import-export/model';
import { ImportExportLevel } from '../../../../src/services/import-export/public_model';
import { OptionItemObject } from '../../../../src/shared_models/interfaces';
import { LevelsAsNumber, LevelsInfo } from '../interfaces';

import { getRandomId } from './utils';

export const mergeWithCurrentOptions = (
    currentOptions: OptionItemObject[],
    levelsInfo: LevelsInfo,
    fileContent: ImportExportLevel
) => {
    const fileContentWithFilledIds = addIdsToNewOptions(fileContent);
    const newOptions = mapToOptionItemObjectRecursive(fileContentWithFilledIds);
    const mergedOptions = mergeOptionsRecursive([...currentOptions], newOptions);
    const mergedLevelInfo = { ...levelsInfo };
    mergeLevelInfoRecursive(mergedLevelInfo, fileContentWithFilledIds);
    addFirstOptionKeyToLevelInfo(mergedLevelInfo, mergedOptions);
    return {
        mergedOptions,
        mergedLevelInfo,
    };
};

const mergeOptionsRecursive = (mergedOptions: OptionItemObject[], newOptions: OptionItemObject[]) =>
    newOptions.reduce((acc, item) => {
        const existingOptionIndex = acc.findIndex(existingOption => existingOption.key === item.key);
        if (existingOptionIndex !== -1) {
            acc[existingOptionIndex] = {
                ...acc[existingOptionIndex],
                ...item,
                children: mergeOptionsRecursive(acc[existingOptionIndex].children || [], item.children || []),
            };
        } else {
            acc.push(item);
        }
        return acc;
    }, mergedOptions);

const mergeLevelInfoRecursive = (levelsInfo: LevelsInfo, level: ImportExportLevel, depth = 1, parentKey: string = 'root') => {
    const existingInfo = levelsInfo[`_${depth}` as LevelsAsNumber].find(item => item.parentOptionKey === parentKey);
    if (existingInfo) {
        levelsInfo[`_${depth}` as LevelsAsNumber] = levelsInfo[`_${depth}` as LevelsAsNumber].filter(
            item => item.parentOptionKey !== parentKey
        );
        levelsInfo[`_${depth}` as LevelsAsNumber].push({
            ...existingInfo,
            levelInfo: {
                ...existingInfo.levelInfo,
                levelDescription: level.description || existingInfo.levelInfo?.levelDescription,
                levelName: level.levelName || existingInfo.levelInfo?.levelName,
            },
        });
    } else if (level.description || level.levelName) {
        levelsInfo[`_${depth}` as LevelsAsNumber].push({
            parentOptionKey: parentKey,
            firstOptionKey: undefined,
            levelInfo: {
                levelDescription: level.description || '',
                levelName: level.levelName || '',
                levelDescriptionTranslation: {},
                levelNameTranslation: {},
            },
        });
    }
    level.options?.forEach(
        option => option.nestedLevel && mergeLevelInfoRecursive(levelsInfo, option.nestedLevel, depth + 1, option.id)
    );
};

const addFirstOptionKeyToLevelInfo = (
    mergedLevelInfo: LevelsInfo,
    mergedOptions: OptionItemObject[],
    depth = 1,
    parentKey: string = 'root'
) => {
    const firstOption = mergedOptions[0];
    if (firstOption) {
        const infoIndex = mergedLevelInfo[`_${depth}` as LevelsAsNumber].findIndex(info => info.parentOptionKey === parentKey);
        if (infoIndex !== -1) {
            mergedLevelInfo[`_${depth}` as LevelsAsNumber][infoIndex] = {
                ...mergedLevelInfo[`_${depth}` as LevelsAsNumber][infoIndex],
                firstOptionKey: firstOption.key,
            };
        }
        mergedOptions.forEach(mergedOption =>
            addFirstOptionKeyToLevelInfo(mergedLevelInfo, mergedOption.children, depth + 1, mergedOption.key)
        );
    }
};

const mapToOptionItemObjectRecursive = (level: ImportExportLevel, depth = 0): OptionItemObject[] =>
    level.options ?
        level.options.map(item => ({
            value: item.value,
            key: item.id!,
            children: item.nestedLevel ? mapToOptionItemObjectRecursive(item.nestedLevel, depth + 1) : [],
            isDefaultOption: item.isDefault !== undefined ? TruthyValueArray.some(value => value === item.isDefault) : false,
            isEnabled: item.isEnabled !== undefined ? TruthyValueArray.some(value => value === item.isEnabled) : true,
            depth,
            translation: {},
        }))
    :   [];

const addIdsToNewOptions = (fileContent: ImportExportLevel): ImportExportLevel => ({
    ...fileContent,
    options: fileContent.options?.map(option => ({
        ...option,
        id: option.id || getRandomId(),
        nestedLevel: option.nestedLevel ? addIdsToNewOptions(option.nestedLevel) : undefined,
    })),
});
